import React, { useState, useEffect, useRef } from 'react';

import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import ChatHeader from './components/ChatHeader';
import ConversationThread from './components/ConversationThread';
import ChatInput from './components/ChatInput';
import SuggestedPrompts from './components/SuggestedPrompts';
import TripSummaryCard from './components/TripSummaryCard';
import SEOHead from '../../components/SEOHead';
import { generateBreadcrumbStructuredData } from '../../utils/seoUtils';
import { generateTripPlanningResponse, generateTripSummaryData, generateSuggestedPrompts } from '../../services/tripPlanningService';
import { testOpenAIConnection } from '../../services/openaiClient';
import Icon from '../../components/AppIcon';
import { useLocation } from 'react-router-dom';




const AITripPlanningInterface = () => {
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [showTripSummary, setShowTripSummary] = useState(false);
  const [currentTripData, setCurrentTripData] = useState(null);
  const [suggestedPrompts, setSuggestedPrompts] = useState([]);
  const [apiError, setApiError] = useState(null);
  const [hasTripPlan, setHasTripPlan] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('checking'); // checking, connected, error

  // Breadcrumb structured data
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'AI Trip Planner', path: '/ai-trip-planning-interface' }
  ];

  const breadcrumbData = generateBreadcrumbStructuredData(breadcrumbs);

  // Test OpenAI connection on component mount
  useEffect(() => {
    const checkConnection = async () => {
      const result = await testOpenAIConnection();
      if (result?.success) {
        setConnectionStatus('connected');
        console.log('✅ OpenAI API connection verified');
      } else {
        setConnectionStatus('error');
        setApiError(result?.message || 'Failed to connect to OpenAI API');
        console.error('❌ OpenAI API connection failed:', result?.error);
      }
    };
    
    checkConnection();
  }, []);

  // Load initial suggested prompts
  useEffect(() => {
    loadSuggestedPrompts();
  }, []);

  // Handle initial prompt from navigation state
  useEffect(() => {
    const initialPrompt = location?.state?.initialPrompt;
    if (initialPrompt && initialPrompt?.trim() && messages?.length === 0) {
      // Automatically send the initial prompt from home page
      handleSendMessage(initialPrompt);
    }
  }, [location?.state?.initialPrompt]);

  // Update suggested prompts when conversation changes
  useEffect(() => {
    if (messages?.length > 0 && messages?.length % 2 === 0) { // After each complete exchange
      loadSuggestedPrompts();
    }
  }, [messages]);

  const loadSuggestedPrompts = async () => {
    try {
      const prompts = await generateSuggestedPrompts(messages);
      setSuggestedPrompts(prompts);
    } catch (error) {
      console.error('Error loading suggested prompts:', error);
    }
  };

  const handleSendMessage = async (messageContent) => {
    // Clear any previous API errors
    setApiError(null);

    // Check connection status before sending
    if (connectionStatus === 'error') {
      setApiError('Your OpenAI API key is invalid or expired. Please update your .env file with a new API key from OpenAI and restart the development server. Check the error banner above for detailed instructions.');
      return;
    }

    // Add user message
    const userMessage = {
      id: Date.now(),
      content: messageContent,
      isUser: true,
      timestamp: new Date()
    };

    setMessages((prev) => [...prev, userMessage]);
    setShowSuggestions(false);
    setIsTyping(true);

    try {
      // **ENHANCED**: Pass userId to integrate travel preferences
      const aiResponseContent = await generateTripPlanningResponse(
        messageContent, 
        messages,
        user?.id // Pass user ID to fetch preferences
      );

      const aiResponse = {
        id: Date.now() + 1,
        content: aiResponseContent,
        isUser: false,
        timestamp: new Date()
      };

      setMessages((prev) => [...prev, aiResponse]);

      // **ENHANCED**: More responsive trip detection with immediate side panel display
      const tripPlanningKeywords = [
        'plan', 'trip', 'visit', 'park', 'camping', 'hiking', 'adventure',
        'travel', 'vacation', 'weekend', 'day', 'night', 'stay', 'go to',
        'recommend', 'suggest', 'help me', 'looking for', 'want to',
        'itinerary', 'book', 'confirm', 'reserve'
      ];

      const hasKeywords = tripPlanningKeywords?.some((keyword) =>
        messageContent?.toLowerCase()?.includes(keyword) ||
        aiResponseContent?.toLowerCase()?.includes(keyword)
      );

      // Show trip planning panel more aggressively
      if (hasKeywords && messages?.length >= 0) {
        setHasTripPlan(true);

        // **CRITICAL FIX**: Always attempt to generate trip data from conversation
        try {
          const tripData = await generateTripSummaryData(
            messageContent, 
            [...messages, userMessage, aiResponse],
            user?.id // Pass user ID to integrate preferences
          );
          
          if (tripData) {
            console.log('✅ Generated conversation-specific trip data:', {
              title: tripData?.title,
              location: tripData?.location,
              highlights: tripData?.highlights?.length
            });
            setCurrentTripData(tripData);
            setShowTripSummary(true);
          } else {
            console.warn('⚠️ Trip data generation returned null, conversation may not contain enough trip planning details');
            // Only show summary if we already have trip data from a previous generation
            if (currentTripData) {
              setShowTripSummary(true);
            }
          }
        } catch (error) {
          console.error('❌ Error generating trip summary:', error);
          // Keep existing trip data if generation fails
          if (currentTripData) {
            setShowTripSummary(true);
          }
        }
      }

    } catch (error) {
      console.error('Error getting AI response:', error);
      
      // Enhanced error message for API key issues
      let errorMsg = 'Unable to connect to OpenAI API. Please check your internet connection and API key configuration.';
      
      if (error?.status === 401 || error?.message?.includes('401') || error?.message?.includes('API key')) {
        errorMsg = '🔑 Authentication Failed: Your OpenAI API key is invalid or expired. Please:\n\n1. Visit https://platform.openai.com/api-keys to generate a new key\n2. Update your .env file: VITE_OPENAI_API_KEY=your_new_key\n3. Restart your development server\n4. Refresh this page';
      }

      setApiError(errorMsg);

      // Add error message to conversation
      const errorResponse = {
        id: Date.now() + 1,
        content: 'I apologize, but I\'m experiencing authentication issues with OpenAI. Your API key appears to be invalid or expired. Please check the error message above for instructions on updating your API key.',
        isUser: false,
        timestamp: new Date(),
        isError: true
      };

      setMessages((prev) => [...prev, errorResponse]);
    } finally {
      setIsTyping(false);
    }
  };

  const handlePromptSelect = (promptText) => {
    handleSendMessage(promptText);
  };

  const handleNewConversation = () => {
    setMessages([]);
    setShowSuggestions(true);
    setShowTripSummary(false);
    setCurrentTripData(null);
    setApiError(null);
    loadSuggestedPrompts(); // Reload default prompts
  };

  const handleDismissTripSummary = () => {
    setShowTripSummary(false);
  };

  const handleToggleTripSummary = () => {
    setShowTripSummary(!showTripSummary);
  };

  return (
    <>
      <SEOHead
        pageType="aiPlanner"
        title="AI Trip Planner - Create Your Perfect State Park Adventure | ParkTrip AI"
        description="Let AI create your personalized state park itinerary. Answer a few questions and get a custom trip plan with activities, routes, and recommendations tailored to your preferences."
        keywords="AI trip planner, personalized itinerary, state park AI, trip planning assistant, outdoor adventure planner, AI travel planner"
        canonicalUrl="https://parktripplannerai.com/ai-trip-planning-interface"
        structuredData={breadcrumbData}
        ogImage="/default-og-image.jpg"
      />

      <div className="min-h-screen bg-background flex flex-col">
        <Header 
          propIsAuthenticated={isAuthenticated} 
          propUser={user} 
          propOnLogout={logout} 
        />
        
        <div className={`flex-1 flex flex-col pt-16 transition-all duration-300 ${showTripSummary ? 'pr-96' : 'pr-0'}`}>
          <ChatHeader
            conversationCount={messages?.length}
            onNewConversation={handleNewConversation}
          />

          
          {/* Connection Status Banner */}
          {connectionStatus === 'checking' && (
            <div className="mx-4 mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-blue-800">Checking OpenAI connection...</p>
                </div>
              </div>
            </div>
          )}

          {connectionStatus === 'connected' && messages?.length === 0 && (
            <div className="mx-4 mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-800">✅ Ranger YogiBot is online and ready to help plan your trip!</p>
                </div>
              </div>
            </div>
          )}

          {connectionStatus === 'error' && (
            <div className="mx-4 mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-red-800">🔑 OpenAI API Key Authentication Failed</h3>
                  <div className="mt-2 text-sm text-red-700 whitespace-pre-wrap">
                    {apiError}
                  </div>
                  <div className="mt-3 space-y-2">
                    <div className="text-sm text-red-700">
                      <strong>How to Fix This:</strong>
                      <ol className="list-decimal ml-4 mt-2 space-y-2">
                        <li>Visit <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline font-medium">OpenAI Platform → API Keys</a></li>
                        <li>Click <strong>&quot;Create new secret key&quot;</strong> and copy your new key</li>
                        <li>Open your project&apos;s <code className="bg-red-100 px-1.5 py-0.5 rounded font-mono">.env</code> file</li>
                        <li>Update or add: <code className="bg-red-100 px-1.5 py-0.5 rounded font-mono">{"VITE_OPENAI_API_KEY=sk-proj-your_actual_key_here"}</code></li>
                        <li><strong>Restart your development server</strong> (Ctrl+C, then npm run dev)</li>
                        <li>Refresh this page</li>
                      </ol>
                    </div>
                    <div className="pt-2 border-t border-red-200">
                      <p className="text-xs text-red-600 mb-2">
                        <strong>Note:</strong> API keys starting with &quot;sk-proj-&quot; are project-specific keys. Make sure your key has the necessary permissions and your OpenAI account has billing enabled.
                      </p>
                    </div>
                    <button
                      onClick={async () => {
                        setApiError(null);
                        setConnectionStatus('checking');
                        const result = await testOpenAIConnection();
                        if (result?.success) {
                          setConnectionStatus('connected');
                        } else {
                          setConnectionStatus('error');
                          setApiError(result?.message);
                        }
                      }}
                      className="text-sm bg-red-100 hover:bg-red-200 text-red-800 font-medium px-3 py-1.5 rounded transition-colors"
                    >
                      🔄 Test Connection Again
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <ConversationThread
            messages={messages}
            isTyping={isTyping}
          />

          
          {showSuggestions && messages?.length === 0 && (
            <SuggestedPrompts
              onPromptSelect={handlePromptSelect}
              isVisible={true}
              prompts={suggestedPrompts}
            />
          )}
          
          <ChatInput
            onSendMessage={handleSendMessage}
            isLoading={isTyping}
            disabled={connectionStatus === 'error'}
          />

        </div>

        <TripSummaryCard
          tripData={currentTripData}
          isVisible={showTripSummary}
          onDismiss={() => setShowTripSummary(false)}
          user={user}
        />

        {/* **ENHANCED**: Always visible and more prominent trip planning button */}
        {(hasTripPlan || currentTripData) && (
          <button
            onClick={handleToggleTripSummary}
            className={`fixed transition-all duration-300 z-30 group ${
              showTripSummary
                ? 'right-[400px] top-24' // Position next to open panel
                : 'right-6 top-1/2 transform -translate-y-1/2' // Floating position when closed
            } ${
              showTripSummary
                ? 'bg-muted hover:bg-muted/80 text-muted-foreground border border-border'
                : 'bg-primary hover:bg-primary/90 text-primary-foreground shadow-organic-lg'
            } rounded-organic p-4 transition-all duration-200 ${
              !showTripSummary ? 'pulse-glow' : ''
            }`}
            aria-label={showTripSummary ? "Close trip summary" : "Open trip summary"}
          >
            {showTripSummary ? (
              <Icon name="ChevronRight" size={24} className="group-hover:scale-110 transition-transform" />
            ) : (
              <div className="flex items-center space-x-2">
                <Icon name="MapPin" size={24} className="group-hover:scale-110 transition-transform" />
                <div className="hidden sm:block">
                  <div className="font-medium text-sm">Trip Ready!</div>
                  <div className="text-xs opacity-90">Click to review</div>
                </div>
              </div>
            )}
            
            {!showTripSummary && (
              <div className="absolute right-full mr-3 top-1/2 transform -translate-y-1/2 bg-background border border-border rounded-organic px-4 py-3 text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-organic">
                <div className="font-medium text-card-foreground">Trip Planning Assistant</div>
                <div className="text-xs text-muted-foreground">Review &amp; organize your trip plan</div>
                <div className="text-xs text-primary mt-1">• Email PDF summary</div>
                <div className="text-xs text-primary">• Save to account</div>
              </div>
            )}
          </button>
        )}

        {/* **ENHANCED**: More visible trip planning prompt */}
        {messages?.length >= 2 && !hasTripPlan && !currentTripData && (
          <div className="fixed bottom-24 right-6 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground rounded-organic p-4 shadow-organic-lg max-w-sm z-20 animate-bounce-gentle">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-primary-foreground/20 rounded-organic flex items-center justify-center flex-shrink-0">
                <Icon name="Sparkles" size={16} className="text-primary-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium mb-1">
                  🎯 Ready to plan your trip?
                </p>
                <p className="text-xs opacity-90 mb-3">
                  Ask me about state parks, camping, or activities to get a personalized trip plan with PDF export!
                </p>
                <button
                  onClick={() => handleSendMessage("Help me plan a 3-day state park adventure with camping and hiking")}
                  className="text-xs bg-primary-foreground text-primary hover:bg-primary-foreground/90 font-medium px-3 py-1 rounded transition-colors"
                >
                  Plan My Trip →
                </button>
              </div>
              <button
                onClick={() => {setHasTripPlan(true);}}
                className="text-primary-foreground/70 hover:text-primary-foreground"
              >
                <Icon name="X" size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .pulse-glow {
          animation: pulse-glow 2s infinite;
        }
        
        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(var(--color-primary-rgb), 0.4);
          }
          50% {
            box-shadow: 0 0 0 10px rgba(var(--color-primary-rgb), 0);
          }
        }
        
        .animate-bounce-gentle {
          animation: bounce-gentle 3s infinite;
        }
        
        @keyframes bounce-gentle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
      `}</style>
    </>
  );
};

export default AITripPlanningInterface;